import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Phone, MapPin, Briefcase, AlertCircle, CheckCircle } from 'lucide-react';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthContext } from '../context/AuthContext';
import { createCompanyProfile, checkCompanyProfile } from '../services/auth';

export const CompanyProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    business_name: '',
    industry: '',
    address: '',
    phone: '',
    business_type: 'product',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.business_name) newErrors.business_name = 'Business name is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const result = await createCompanyProfile(formData);
      if (result.success) {
        
        // Now check company profile to get the company_id
        if (user && user.id) {
          const token = localStorage.getItem('token');
          const profileCheck = await checkCompanyProfile(user.id, token);
          
          if (profileCheck.success && profileCheck.company_id) {
            localStorage.setItem('company_id', profileCheck.company_id);
          }
        }
        
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to create company profile' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md mx-4 sm:mx-0 border border-blue-100 dark:border-gray-700">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg transform hover:scale-105 transition">
              <Building2 className="text-white w-8 h-8" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create Company Profile</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium text-base">Tell us about your business</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700 text-sm font-medium">
            <CheckCircle className="w-5 h-5" />
            <span>Company profile created! Redirecting to dashboard...</span>
          </div>
        )}

        {/* Error Message */}
        {errors.submit && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm font-medium">
            <AlertCircle className="w-5 h-5" />
            <span>{errors.submit}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Business Name Field */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1 text-base">Business Name</label>
            <div className="relative">
              <Building2 className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="business_name"
                placeholder="Your company name"
                value={formData.business_name}
                onChange={handleChange}
                className={`w-full px-4 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base font-medium transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                  errors.business_name ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                }`}
              />
            </div>
            {errors.business_name && <p className="text-red-600 text-xs mt-1">{errors.business_name}</p>}
          </div>

          {/* Industry Field */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1 text-base">Industry</label>
            <div className="relative">
              <Briefcase className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="industry"
                placeholder="e.g., Technology, Retail, Finance"
                value={formData.industry}
                onChange={handleChange}
                className={`w-full px-4 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base font-medium transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                  errors.industry ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                }`}
              />
            </div>
            {errors.industry && <p className="text-red-600 text-xs mt-1">{errors.industry}</p>}
          </div>

          {/* Address Field */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1 text-base">Address</label>
            <div className="relative">
              <MapPin className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="address"
                placeholder="Your business address"
                value={formData.address}
                onChange={handleChange}
                className={`w-full px-4 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base font-medium transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                  errors.address ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                }`}
              />
            </div>
            {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1 text-base">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                placeholder="Your phone number"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base font-medium transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                  errors.phone ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                }`}
              />
            </div>
            {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
          </div>

          {/* Business Type Field */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1 text-base">Business Type</label>
            <select
              name="business_type"
              value={formData.business_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base font-medium transition"
            >
              <option value="product">Product</option>
              <option value="service">Service</option>
              <option value="both">Both Product & Service</option>
            </select>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors shadow"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating Profile...
              </span>
            ) : (
              'Create Company Profile'
            )}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
};
